import * as React from 'react';
import { Form, DatePickerRange, TextInput, Select, CustomSubmitButton, Modal, ModalBody, ModalFooter, ButtonType, CustomButton, CustomSubmitButtonType } from "soprasteria-ui-library";
import { useAuth } from '../../../../App';
import { CareProviderDropdown } from '../../../CareProviderDropdown/CareProviderDropdown';
import { ISearchInvoice } from '../../../../models/IInvoice';
import { SearchInvoiceApi } from '../../../../api/SearchInvoiceApi';
import { ICareProvider } from '../../../../models/ICareProvider';
import { IListItem } from 'soprasteria-ui-library/dist/models/IFormInput';
import { InvoiceApi } from '../../../../api/InvoiceApi';
import { isInRole, UserContext } from '../../../../contexts/UserContext';
import { ATTESTERAFAKTUROR, HANDLÄGGAFAKTUROR, LIBRETTOADMIN, VÅRDANSVARIG } from '../../../../utils/RoleConstants';
import { InvoiceAdminsistrationApi } from '../../../../api/InvoiceAdminsistrationApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';

interface ISearchInvoiceForm {
    preSetSearchParams?: ISearchInvoice;
    searchInvoices: (data: ISearchInvoice) => void;
    isFetchingInvoices: boolean;
    changeSuccessMessage: (message: string) => void;
}

export const SearchInvoiceForm: React.FunctionComponent<ISearchInvoiceForm> = ({ searchInvoices, preSetSearchParams, isFetchingInvoices, changeSuccessMessage }) => {

    const auth = useAuth();
    const searchInvoiceApi = new SearchInvoiceApi(auth);
    const invoiceApi = new InvoiceApi(auth);
    const invoiceAdministrationApi = new InvoiceAdminsistrationApi(auth);
    const abortController = new AbortController();
    const [invoiceStatuses, setInvoiceStatuses] = React.useState<IListItem[]>();
    const [treatmentTypes, setTreatmentTypes] = React.useState<IListItem[]>();
    const [selectedStatusId, setSelectedStatusId] = React.useState<string>();
    const [isApprovingInvoices, setIsApprovingInvoices] = React.useState<boolean>(false);
    const [isAttestingInvoices, setIsAttestingInvoices] = React.useState<boolean>(false);
    const [useDefaultValue, setUseDefaultValue] = React.useState<boolean>(true);
    const [isFetchingUmbrellaOrganisations, setIsFetchingUmbrellaOrganisations] = React.useState<boolean>(false);
    const [isFetchingCareProviders, setIsFetchingCareProviders] = React.useState<boolean>(false);
    const [umbrellaOrganisations, setUmbrellaOrganisations] = React.useState<ICareProvider[]>([]);
    const [careProviders, setCareProviders] = React.useState<ICareProvider[]>();
    const [selectedUmbrellaOrganisationId, setSelectedUmbrellaOrganisationId] = React.useState<number>();
    const [resetCareProviderValue, doResetCareProviderValue] = React.useState<boolean>(false);
    const [selectedTreatmentTypeId, setSelectedTreatmentTypeId] = React.useState<string>();
    const [isCareProviderSelected, setIsCareProviderSelected] = React.useState<boolean>(false);
    const sentStatusId = "1";
    const approvedStatusId = "7";
    const userInfo = React.useContext(UserContext);
    const hasRoleFakturaGodkannare = isInRole(HANDLÄGGAFAKTUROR, userInfo.roles);
    const hasRoleFakturaAttesterare = isInRole(ATTESTERAFAKTUROR, userInfo.roles);
    const hasRoleSystemAdministrator = isInRole(LIBRETTOADMIN, userInfo.roles);
    const hasRoleParaply = isInRole(VÅRDANSVARIG, userInfo.roles);
    const [isExportingToExcel, setIsExportingToExcel] = React.useState<boolean>(false);

    React.useEffect(() => {
        getUmbrellaOrganisations();
        getInvoiceStatuses();
        getTreatmentTypes();
        getInvoiceStatusDefaultValue();

        return () => {
            abortController.abort();
        }
    }, [])

    React.useEffect(() => {
        if ((hasRoleParaply || hasRoleSystemAdministrator) && selectedUmbrellaOrganisationId) {
            if (!isCareProviderSelected) {
                getCareProvidersByUmbrellaCareProviderId(selectedUmbrellaOrganisationId);
            }            
        }
    }, [selectedTreatmentTypeId])

    const getUmbrellaOrganisations = () => {
        setIsFetchingUmbrellaOrganisations(true);
        searchInvoiceApi.getUmbrellaOrganisations((umbrellaOrganisations) => {
            let umbrellaCareProviderIdToUse;
            if (preSetSearchParams?.umbrellaCareProviderId) {
                umbrellaCareProviderIdToUse = parseInt(preSetSearchParams.umbrellaCareProviderId.toString());
                setSelectedUmbrellaOrganisationId(umbrellaCareProviderIdToUse)
                getCareProvidersByUmbrellaCareProviderId(umbrellaCareProviderIdToUse, umbrellaOrganisations);
            }
            else if (!hasRoleSystemAdministrator) {
                umbrellaCareProviderIdToUse = umbrellaOrganisations[0].careProviderId;
                setSelectedUmbrellaOrganisationId(umbrellaOrganisations[0].careProviderId);
                getCareProvidersByUmbrellaCareProviderId(umbrellaCareProviderIdToUse, umbrellaOrganisations);
            }

            setUmbrellaOrganisations(umbrellaOrganisations);
        }, undefined, () => {
            setIsFetchingUmbrellaOrganisations(false);
        });
    }

    const getCareProvidersByUmbrellaCareProviderId = (umbrellaCareProviderId: number, umbrellaOrganisationsAlt?: ICareProvider[]) => {
        setIsFetchingCareProviders(true);

        searchInvoiceApi.getCareProvidersByUmbrellaCareProviderId(umbrellaCareProviderId, selectedTreatmentTypeId!, (careProviders) => {
            if (careProviders.length !== 0) {
                setCareProviders(careProviders);
            } else {
                setCareProviders(undefined);
            }
            setIsFetchingCareProviders(false);
        }, undefined, () => { });
    }

    const onUmbrellaCareProviderChange = (selectedCareProvider: string | undefined) => {
        if (hasRoleSystemAdministrator) {
            if (selectedCareProvider && selectedCareProvider !== "") {
                getCareProvidersByUmbrellaCareProviderId(parseInt(selectedCareProvider));
                setSelectedUmbrellaOrganisationId(parseInt(selectedCareProvider));
            }
            else {
                setSelectedUmbrellaOrganisationId(undefined);
                setCareProviders([]);
            }
            doResetCareProviderValue(prev => !prev);
        }
    }

    const onCareProviderChange = (selectedCareProvider: string | undefined) => {
        if (selectedCareProvider) {
            setIsCareProviderSelected(true);
        } else {
            setIsCareProviderSelected(false);
        }
    }

    const getInvoiceStatuses = () => {
        searchInvoiceApi.getInvoiceStatuses((data) => {
            const invoiceStatusesListItems = data.map<IListItem>((status) => { return { value: status.invoiceStatusId.toString(), text: status.description } });
            setInvoiceStatuses(invoiceStatusesListItems);
        })
    }

    const getTreatmentTypes = () => {
        invoiceApi.getTreatmentTypes((data) => {
            const treatmentTypeListItems = data.map<IListItem>((treatmentType) => { return { value: treatmentType.treatmentTypeId.toString(), text: treatmentType.description } });
            setTreatmentTypes(treatmentTypeListItems);
        })
    }

    const getTreatmentTypesDefaultValue = () => {
        if (treatmentTypes?.length === 1) {
            return treatmentTypes[0].value;
        }
        if (preSetSearchParams?.treatmentTypeId) {
            return treatmentTypes?.find((type) => type.value === preSetSearchParams.treatmentTypeId?.toString())?.value;
        }

        return undefined;
    }

    const getUmbrellaCareProviderDefaultValue = () => {
        if (umbrellaOrganisations && preSetSearchParams?.umbrellaCareProviderId) {
            return preSetSearchParams.umbrellaCareProviderId.toString();
        }

        if (selectedUmbrellaOrganisationId) {
            return selectedUmbrellaOrganisationId.toString();
        }

        if (umbrellaOrganisations?.length === 1) {
            return umbrellaOrganisations[0].careProviderId.toString();
        }

        return undefined;
    }

    const getCareProviderDefaultValue = () => {
        if (careProviders) {
            const preSetCareProviderIdCasted = preSetSearchParams?.careProviderId
                ? parseInt(preSetSearchParams.careProviderId.toString())
                : undefined;

            const preSetCareProviderIdExistInCareProviders = preSetCareProviderIdCasted !== undefined
                ? careProviders.some((careProvider) => careProvider.careProviderId === preSetCareProviderIdCasted)
                : undefined;

            if (careProviders && preSetCareProviderIdExistInCareProviders) {
                return preSetSearchParams?.careProviderId?.toString();
            }

            if (careProviders && careProviders.length === 1 && careProviders[0] !== undefined) {
                return careProviders[0].careProviderId.toString();
            }
        }

        return undefined;
    }

    const getInvoiceStatusDefaultValue = () => {
        if (preSetSearchParams?.statusId) {
            if (selectedStatusId === undefined && useDefaultValue) {
                setUseDefaultValue(false);
                setSelectedStatusId(preSetSearchParams.statusId.toString())
            }
            return preSetSearchParams.statusId.toString();
        }
        return undefined;
    }

    const approveInvoices = (data: ISearchInvoice) => {
        setIsApprovingInvoices(true);
        invoiceAdministrationApi.approveInvoices(data, () => {
            document.getElementById("close-approve-invoices-modal")?.click();
            changeSuccessMessage("Fakturor har godkänts utifrån dem valda sökparametrarna");
            searchInvoices(data);
        }, undefined, () => {
            setIsApprovingInvoices(false);
        });
    }

    const attestInvoices = (data: ISearchInvoice) => {
        setIsAttestingInvoices(true);
        invoiceAdministrationApi.attestInvoices(data, () => {
            document.getElementById("close-attest-invoices-modal")?.click();
            changeSuccessMessage("Fakturor har attesterats utifrån dem valda sökparametrarna");
            searchInvoices(data);
        }, undefined, () => {
            setIsAttestingInvoices(false);
        });
    }

    const exportSearchResultToExcel = (data: ISearchInvoice) => {
        setIsExportingToExcel(true);

        searchInvoiceApi.searchInvoices(data, true, undefined, "Fakturor", undefined, () => setIsExportingToExcel(false));
    }

    return (
        <section>
            <Form >
                <div className="row">
                    <div className="col-6">
                        <DatePickerRange label="Fakturadatum" name="invoiceDateFrom" nameSecondary="invoiceDateTo" inlineLabel
                            value={preSetSearchParams?.invoiceDateFrom ? new Date(preSetSearchParams.invoiceDateFrom) : undefined}
                            valueSecondary={preSetSearchParams?.invoiceDateTo ? new Date(preSetSearchParams.invoiceDateTo) : undefined} />
                        <TextInput name="invoiceReference" label="Extern fakturareferens" inlineLabel defaultValue={preSetSearchParams?.invoiceReference} />
                        <CareProviderDropdown
                            name="umbrellaCareProviderId"
                            label="Vårdgivare"
                            isLoading={isFetchingUmbrellaOrganisations}
                            careProviders={umbrellaOrganisations}
                            onValueChange={onUmbrellaCareProviderChange}
                            defaultValue={getUmbrellaCareProviderDefaultValue()}
                            disabled={!hasRoleSystemAdministrator}
                            useFixedListItemHeight
                            showOrgNum
                            placeholder="Välj vårdgivare"
                            isClearable
                        />
                        <CareProviderDropdown
                            name="careProviderId"
                            label="Mottagning"
                            isLoading={isFetchingCareProviders}
                            onValueChange={onCareProviderChange}
                            careProviders={careProviders ?? []}
                            defaultValue={getCareProviderDefaultValue()}
                            disabled={!(hasRoleParaply || hasRoleSystemAdministrator)}
                            useFixedListItemHeight
                            showClinicId
                            isClearable
                            resetValue={resetCareProviderValue}
                        />
                    </div>
                    <div className="col-6">
                        <Select name="statusId" label="Fakturastatus" placeholder="Välj en status" options={invoiceStatuses ?? []} selectedValue={preSetSearchParams?.statusId?.toString() ?? selectedStatusId} isLoading={invoiceStatuses === undefined} inlineLabel isClearable
                            onChange={(val) => setSelectedStatusId(val)} />
                        <Select name="treatmentTypeId" label="Fakturatyp" options={treatmentTypes ?? []} onChange={(val) => setSelectedTreatmentTypeId(val)} selectedValue={getTreatmentTypesDefaultValue()} isLoading={treatmentTypes === undefined} placeholder="Välj en fakturatyp" inlineLabel isClearable />
                        <TextInput name="invoiceVerNr" label="Verifikationsnummer" inlineLabel defaultValue={preSetSearchParams?.invoiceVerNr} />
                    </div>
                    <div className="col-12">
                        <div className="text-right panel">
                            <span className="d-none">
                                <CustomSubmitButton onButtonSubmit={searchInvoices} isLoading={isFetchingInvoices}>Sök</CustomSubmitButton>
                            </span>
                            <CustomSubmitButton buttonType={CustomSubmitButtonType.alternative} onButtonSubmit={exportSearchResultToExcel} isLoading={isExportingToExcel}><FontAwesomeIcon icon={faFileExcel} /> Exportera till excel</CustomSubmitButton>
                            {
                                hasRoleFakturaGodkannare && selectedStatusId && selectedStatusId === sentStatusId ?
                                    <CustomButton buttonText="Godkänn fakturor" buttonType={ButtonType.createAlt} data-toggle="modal" data-target="#approve-invoices-modal" />
                                    : null
                            }
                            {
                                hasRoleFakturaAttesterare && selectedStatusId && selectedStatusId === approvedStatusId ?
                                    <CustomButton buttonText="Attestera fakturor" buttonType={ButtonType.createAlt} data-toggle="modal" data-target="#attest-invoices-modal" />
                                    : null
                            }
                            <CustomSubmitButton id="submitbutton" onButtonSubmit={searchInvoices} isLoading={isFetchingInvoices}>Sök</CustomSubmitButton>
                        </div>
                    </div>
                </div>
                <Modal header="Godkänn fakturor" id="approve-invoices-modal">
                    <ModalBody>
                        <p>Är du säker på att du vill godkänna fakturor utfrån dem valda sökparametrarna?</p>
                    </ModalBody>
                    <ModalFooter>
                        <CustomButton buttonType={ButtonType.general} buttonText="Avbryt" data-dismiss="modal" id="close-approve-invoices-modal" />
                        <CustomSubmitButton onButtonSubmit={approveInvoices} isLoading={isApprovingInvoices}>Godkänn</CustomSubmitButton>
                    </ModalFooter>
                </Modal>
                <Modal header="Attestera fakturor" id="attest-invoices-modal">
                    <ModalBody>
                        <p>Är du säker på att du vill attestera fakturor utfrån de valda sökparametrarna? Endast fakturor som du inte själv godkänt kommer att attesteras</p>
                    </ModalBody>
                    <ModalFooter>
                        <CustomButton buttonType={ButtonType.general} buttonText="Avbryt" data-dismiss="modal" id="close-attest-invoices-modal" />
                        <CustomSubmitButton onButtonSubmit={attestInvoices} isLoading={isAttestingInvoices}>Attestera</CustomSubmitButton>
                    </ModalFooter>
                </Modal>
            </Form>
        </section>
    )
}